import { CUBIC_METER_IN_CUBIC_FEET } from '../constants/VolumeUnits';
import { PaymentUnit } from '../graphql';
import { exhaustiveCheck } from './util';
import type { OrderPorts } from './worldscaleFreight';
import {
  calculateWorldscaleFreight,
  type WorldscaleFreightRateMap,
} from './worldscaleFreight';

/**
 * Calculates billable quantity for FRT (Freight Rate Ton).
 * FRT follows "weight or measure, whichever is greater" (W/M) principle.
 * Standard conversion: 1 MT = 1 CBM for comparison.
 * Returns null only if both mass and volume are null.
 */

export function calculateFrtBillableQuantity(
  mass: number | null,
  volume: number | null,
): number | null {
  if (mass === null && volume === null) return null;
  return Math.max(mass ?? 0, volume ?? 0);
}

export interface FreightCalculationInput {
  mass: number | null;
  volume: number | null;
  pcs: number | null;
  freightRate: number;
  freightUnit: PaymentUnit;
  worldscaleFreightRates?: WorldscaleFreightRateMap;
  order?: OrderPorts;
}

/**
 * Core freight calculation logic shared across income and cost calculations.
 * Handles all payment unit types and returns the calculated freight amount.
 */
export function calculateFreightAmount({
  mass,
  volume,
  pcs,
  freightRate,
  freightUnit,
  worldscaleFreightRates,
  order,
}: FreightCalculationInput): number | null {
  switch (freightUnit) {
    case PaymentUnit.LUMPSUM: {
      return freightRate;
    }
    case PaymentUnit.PER_SET:
    case PaymentUnit.PER_PCS: {
      if (pcs === null) return null;
      return pcs * freightRate;
    }
    case PaymentUnit.FT3: {
      if (volume === null) return null;
      return volume * CUBIC_METER_IN_CUBIC_FEET * freightRate;
    }
    case PaymentUnit.M3:
    case PaymentUnit.SCBM_UB: {
      if (volume === null) return null;
      return volume * freightRate;
    }
    case PaymentUnit.BDMT:
    case PaymentUnit.MT: {
      if (mass === null) return null;
      return mass * freightRate;
    }
    case PaymentUnit.WORLDSCALE: {
      const worldscaleResult = calculateWorldscaleFreight(
        freightRate,
        worldscaleFreightRates,
        order,
        mass,
      );
      return worldscaleResult ? worldscaleResult.freight : null;
    }
    case PaymentUnit.FRT: {
      const billableQuantity = calculateFrtBillableQuantity(mass, volume);
      if (billableQuantity === null) return null;
      return billableQuantity * freightRate;
    }
    default: {
      exhaustiveCheck(freightUnit);
    }
  }
}
